import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ChevronDown, MapPin, MessageSquareText, ShieldCheck, Wrench, Gauge, Clock, BatteryCharging } from "lucide-react";
import SEO, { localBusinessJsonLd } from "@/components/SEO";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import WheelVisual from "@/components/WheelVisual";
import DispatchComposer from "@/components/DispatchComposer";
import SectionHeading from "@/components/SectionHeading";
import FAQAccordion from "@/components/FAQAccordion";
import ServiceIcon, { SERVICE_ICON_BY_SLUG } from "@/components/ServiceIcon";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import { PHONE_TEL, PHONE_DISPLAY, openWa } from "@/lib/wa";

const lineWrap = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const ICONS = { "map-pin": MapPin, "message-circle": MessageSquareText, "shield-check": ShieldCheck, wrench: Wrench, gauge: Gauge, clock: Clock, "circle-dot": Wrench, "battery-charging": BatteryCharging, "loader-pinwheel": Wrench, ruler: Gauge, "life-buoy": ShieldCheck };

export default function Home() {
  const { t, pick } = useLang();
  const { settings, zones, services, neighborhoods, faqs } = useData();
  const { scrollY } = useScroll();
  const vanY = useTransform(scrollY, [0, 600], [0, 60]);
  const wheelY = useTransform(scrollY, [0, 600], [0, -50]);

  const hero = settings?.hero || {};

  return (
    <main>
      <SEO
        title={pick(settings?.seo?.title_ar, settings?.seo?.title_en) || "ميرو للإطارات وخدمات السيارات | بنشر متنقل في الرياض"}
        description={pick(settings?.seo?.desc_ar, settings?.seo?.desc_en)}
        path="/"
        image={settings?.hero?.image}
        jsonLd={localBusinessJsonLd()}
      />

      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden pt-[72px]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 start-[8%] h-[480px] w-[480px] rounded-full bg-gold/[0.08] blur-[150px]" />
          <div className="absolute bottom-0 end-[5%] h-[380px] w-[380px] rounded-full bg-gold/[0.05] blur-[120px]" />
        </div>

        <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-5 py-14 lg:grid-cols-[1.05fr_1fr] lg:gap-8 lg:px-8">
          <div>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow mb-6">
              {pick(hero.eyebrow_ar, hero.eyebrow_en) || "MERO TIRES & AUTO SERVICES"}
            </motion.p>

            <h1 className="text-4xl font-extrabold leading-[1.35] tracking-tight text-white sm:text-5xl lg:text-[3.4rem] lg:leading-[1.3]" data-testid="hero-title">
              {(pick(hero.title_ar, hero.title_en) || ["ميرو.. خدمات الإطارات", "تصلك أينما كنت"]).map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span variants={lineWrap} custom={i} initial="hidden" animate="show" className={i === 1 ? "gold-text" : ""}>
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }} className="mt-6 max-w-xl text-base leading-9 text-mist sm:text-lg">
              {pick(hero.subtitle_ar, hero.subtitle_en)}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }} className="mt-9 flex flex-wrap items-center gap-3.5">
              <Link to="/order" data-testid="hero-order-cta" className="btn-gold animate-pulse-gold !px-8 !py-4">{t("cta.orderNow")}</Link>
              <button onClick={() => openWa()} data-testid="hero-whatsapp-cta" className="btn-wa"><MessageCircle size={18} /> {t("cta.whatsapp")}</button>
              <a href="#services" data-testid="hero-explore-cta" className="btn-ghost">{t("cta.explore")}</a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-sm text-mist">
              <span><strong className="font-display text-2xl text-gold">{neighborhoods.length || 49}</strong> {t("home.statNeighborhoods")}</span>
              <span><strong className="font-display text-2xl text-gold">{services.length || 8}</strong> {t("home.statServices")}</span>
              <span><strong className="font-display text-2xl text-gold">{zones.length || 9}</strong> {t("home.statZones")}</span>
            </motion.div>
          </div>

          <div className="relative">
            <motion.div style={{ y: vanY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl shadow-black/60">
              <img src={hero.image} alt="سيارة بنشر متنقل من ميرو في الرياض" className="h-[300px] w-full object-cover object-[30%_center] sm:h-[420px] lg:h-[480px]" data-testid="hero-van-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            </motion.div>

            <motion.div style={{ y: wheelY }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="absolute -bottom-8 start-4 hidden items-center gap-4 rounded-3xl border border-gold/25 bg-coal/90 p-5 backdrop-blur-xl sm:flex">
              <WheelVisual className="h-20 w-20" />
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-gold">TPMS LIVE</p>
                <p className="mt-1 text-sm font-semibold text-white">32 <span className="text-dim">PSI · 2.2 Bar</span></p>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.a href="#quick-strip" className="absolute bottom-5 start-1/2 -translate-x-1/2 text-gold/60" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} aria-label="scroll">
          <ChevronDown size={26} />
        </motion.a>
      </section>

      {/* QUICK STRIP */}
      <section id="quick-strip" className="border-y border-white/5 bg-coal/60 py-10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 lg:grid-cols-4 lg:px-8">
          {[
            { icon: "tire", ar: "تغيير الإطارات", sub: "جميع الأنواع والمقاسات" },
            { icon: "puncture", ar: "إصلاح البنشر", sub: "بالطريقة الصحيحة للثقوب القابلة للإصلاح" },
            { icon: "tpms", ar: "برمجة حساسات الكفرات", sub: "لمبة الحساس في لوحة القيادة" },
            { icon: "battery", ar: "تغيير البطاريات", sub: "فحص واستبدال في موقعك" },
          ].map((s, i) => (
            <Reveal key={i} delay={i} className="flex items-center justify-center gap-4 sm:justify-start">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-gold/70 text-gold"><ServiceIcon icon={s.icon} size={24} /></span>
              <span>
                <span className="block font-bold text-white">{s.ar}</span>
                <span className="mt-1 block text-xs text-mist">{s.sub}</span>
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={(settings?.marquee_ar || []).length ? settings.marquee_ar : ["تغيير الإطارات", "إصلاح البنشر", "برمجة حساسات الكفرات", "ترصيص الإطارات", "تغيير البطاريات"]} />

      {/* COMPOSER */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow={{ ar: "طلب سريع", en: "Quick order" }} title={{ ar: t("home.composerTitle"), en: t("home.composerTitle") }} sub={{ ar: t("home.composerSub"), en: t("home.composerSub") }} />
          <Reveal><DispatchComposer services={services} neighborhoods={neighborhoods} /></Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-coal/40 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow={{ ar: "خدماتنا", en: "Services" }} title={{ ar: t("home.servicesTitle"), en: t("home.servicesTitle") }} sub={{ ar: t("home.servicesSub"), en: t("home.servicesSub") }} />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-grid">
            {services.slice(0, 8).map((s, i) => (
              <Reveal key={s.slug} delay={i % 4}>
                <Link to={s.slug === "tpms" ? "/tpms" : s.has_page ? `/services/${s.slug}` : s.slug === "tire-size-help" ? "/tires" : "/contact"} className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink/70 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/40" data-testid={`service-card-${s.slug}`}>
                  <span className="pointer-events-none absolute -end-8 -top-8 h-24 w-24 rounded-full bg-gold/0 blur-2xl transition-all duration-500 group-hover:bg-gold/15" />
                  <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold"><ServiceIcon icon={SERVICE_ICON_BY_SLUG[s.slug] || "other"} size={22} /></span>
                  <h3 className="text-lg font-bold text-white group-hover:text-gold">{pick(s.title_ar, s.title_en)}</h3>
                  <p className="mt-3 flex-1 text-sm leading-7 text-mist">{pick(s.short_ar, s.short_en)}</p>
                  <span className="mt-5 flex items-center gap-2 text-sm font-bold text-gold opacity-0 transition-all duration-300 group-hover:opacity-100">
                    {t("cta.readMore")} <ArrowIcon />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/services" data-testid="home-all-services" className="btn-ghost">{t("cta.viewAll")}</Link>
          </Reveal>
        </div>
      </section>

      {/* WHY MERO (light) */}
      <section className="relative overflow-hidden bg-[#F4F2EC] py-20 text-ink sm:py-28" data-testid="why-mero-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <Reveal>
              <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#a3820a]">WHY MERO</p>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{t("home.whyTitle")}</h2>
              <p className="mt-4 text-base leading-8 text-zinc-600">{t("home.whySub")}</p>
            </Reveal>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(settings?.features || []).map((f, i) => {
              const Icon = ICONS[f.icon] || MapPin;
              return (
                <Reveal key={i} delay={i % 3}>
                  <div className="group h-full rounded-3xl border border-black/5 bg-white p-8 shadow-[0_10px_40px_rgba(8,11,16,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(8,11,16,0.12)]">
                    <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-gold transition-transform duration-300 group-hover:scale-110"><Icon size={22} /></span>
                    <h3 className="text-lg font-bold text-ink">{pick(f.ar, f.en)}</h3>
                    <p className="mt-3 text-sm leading-7 text-zinc-600">{pick(f.desc_ar, f.desc_en)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <img src="https://static.prod-images.emergentagent.com/jobs/fda92389-ba5f-436d-8064-e3275d1507fa/images/efe9352979ac2e478240201d23c1acd9dd89a628a22cf0c7084f97297f273835.jpeg" alt="سيارة خدمة ميرو في شوارع الرياض" className="w-full rounded-[2rem] border border-white/10 object-cover shadow-2xl shadow-black/50" data-testid="coverage-image" />
          </Reveal>
          <div>
            <SectionHeading eyebrow={{ ar: "التغطية", en: "Coverage" }} title={{ ar: t("home.coverageTitle"), en: t("home.coverageTitle") }} sub={{ ar: t("home.coverageSub"), en: t("home.coverageSub") }} />
            <Reveal>
              <div className="flex flex-wrap gap-2.5" data-testid="coverage-zone-chips">
                {zones.map((z) => (
                  <Link key={z.id} to="/riyadh" className="rounded-full border border-white/10 bg-coal/70 px-4 py-2 text-sm text-zinc-300 transition-all hover:border-gold/50 hover:text-gold">
                    {pick(z.ar, z.en)}
                  </Link>
                ))}
              </div>
              <Link to="/riyadh" data-testid="home-coverage-cta" className="btn-gold mt-8">{t("home.coverageCta")}</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="bg-coal/40 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading eyebrow={{ ar: "المدونة", en: "Blog" }} title={{ ar: t("home.blogTitle"), en: t("home.blogTitle") }} sub={{ ar: t("home.blogSub"), en: t("home.blogSub") }} />
          <BlogTeaser />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <SectionHeading center eyebrow={{ ar: "الأسئلة الشائعة", en: "FAQ" }} title={{ ar: t("home.faqTitle"), en: t("home.faqTitle") }} sub={{ ar: t("home.faqSub"), en: t("home.faqSub") }} />
          <FAQAccordion items={(faqs || []).map((f) => ({ q: pick(f.q_ar, f.q_en), a: pick(f.a_ar, f.a_en) }))} />
          <Reveal className="mt-8 text-center">
            <Link to="/faq" className="btn-ghost" data-testid="home-faq-all">{t("cta.viewAll")}</Link>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden border-t border-gold/20 py-20">
        <img src="https://static.prod-images.emergentagent.com/jobs/fda92389-ba5f-436d-8064-e3275d1507fa/images/c61e63e7f7a86e2c47a9ef8f3e568678ce288c16a199374f32f2f2c7b6ef1daf.jpeg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink" />
        <div className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">ميرو.. خدمات الإطارات <span className="gold-text">تصلك أينما كنت</span></h2>
            <p className="mx-auto mt-4 max-w-xl text-mist">راسلنا الآن عبر واتساب أو اتصل مباشرة — 0507359999</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3.5">
              <button onClick={() => openWa()} className="btn-wa" data-testid="home-band-wa"><MessageCircle size={18} /> {t("cta.whatsapp")}</button>
              <a href={PHONE_TEL} className="btn-gold" data-testid="home-band-call"><Phone size={18} /> {PHONE_DISPLAY}</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function ArrowIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function BlogTeaser() {
  const { pick } = useLang();
  const { categories } = useData();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => api.get("/articles", { params: { limit: 3 } }).then(({ data }) => setPosts(data)).catch(() => {}));
  }, []);
  const catName = (id) => pick((categories.find((c) => c.id === id) || {}).name_ar, (categories.find((c) => c.id === id) || {}).name_en) || id;

  return (
    <div className="grid gap-6 md:grid-cols-3" data-testid="home-blog-teaser">
      {posts.map((p, i) => (
        <Reveal key={p.slug} delay={i}>
          <Link to={`/blog/${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink/70 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/40" data-testid={`home-blog-card-${p.slug}`}>
            <div className="h-44 overflow-hidden">
              <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-1 flex-col p-6">
              <p className="text-xs font-bold text-gold">{catName(p.category)}</p>
              <h3 className="mt-2 flex-1 text-base font-bold leading-8 text-white group-hover:text-gold">{p.title}</h3>
              <span className="mt-4 flex items-center gap-2 text-xs text-dim">{p.read_time} {pick("دقائق قراءة", "min read")}</span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  );
}
